/**
 * Sliders double poignée du drawer de filtres (maquette Figma 269:1659 / 286:23).
 *
 * Deux variantes partagent la même mécanique de poignées :
 *  - <cv-price-slider>  pilote les deux champs texte du `price-range` Shopify ;
 *    ce sont eux qui portent les `name` envoyés dans la requête.
 *  - <cv-bucket-slider> pilote un filtre liste dont les valeurs sont des paliers
 *    (les remises : -20% … -70%). Le slider est continu à l'écran, mais coche
 *    en réalité les paliers compris dans l'intervalle choisi.
 *
 * Dans les deux cas le formulaire de facettes écoute `input` : c'est en
 * rediffusant cet évènement qu'on déclenche le re-rendu de la grille.
 */

/** Socle commun : deux <input type="range"> superposés qui ne se croisent pas. */
class CvRangeSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.rangeBar = this.querySelector('.cvf__slider-range');
    this.minRange = this.querySelector('.cvf__slider-input--min');
    this.maxRange = this.querySelector('.cvf__slider-input--max');

    if (!this.minRange || !this.maxRange) return;

    this.min = Number(this.dataset.min) || 0;
    this.max = Number(this.dataset.max) || 0;

    this.minRange.addEventListener('input', () => this.onRangeInput('min'));
    this.maxRange.addEventListener('input', () => this.onRangeInput('max'));
    // `change` ne part qu'au relâchement : une seule requête par manipulation.
    this.minRange.addEventListener('change', () => this.commit());
    this.maxRange.addEventListener('change', () => this.commit());

    this.setup();
    this.paint();
  }

  /** Point d'extension : câblage propre à chaque variante. */
  setup() {}

  /** Point d'extension : report des valeurs sur les champs affichés. */
  render() {}

  /** Point d'extension : envoi des valeurs au formulaire de facettes. */
  commit() {}

  get values() {
    return { min: Number(this.minRange.value), max: Number(this.maxRange.value) };
  }

  onRangeInput(origin) {
    let { min: minValue, max: maxValue } = this.values;

    if (minValue > maxValue) {
      if (origin === 'min') {
        minValue = maxValue;
        this.minRange.value = minValue;
      } else {
        maxValue = minValue;
        this.maxRange.value = maxValue;
      }
    }

    this.render();
    this.paint();
  }

  /** Rediffuse un `input` bouillonnant pour réveiller <facet-filters-form>. */
  notify() {
    this.dispatchEvent(new Event('input', { bubbles: true }));
  }

  paint() {
    if (!this.rangeBar || this.max <= this.min) return;

    const span = this.max - this.min;
    const left = ((Number(this.minRange.value) - this.min) / span) * 100;
    const right = 100 - ((Number(this.maxRange.value) - this.min) / span) * 100;

    this.rangeBar.style.left = `${left}%`;
    this.rangeBar.style.right = `${right}%`;

    // La poignee la plus proche du bord passe au-dessus pour rester attrapable.
    this.minRange.style.zIndex = left > 50 ? 3 : 2;
    this.maxRange.style.zIndex = left > 50 ? 2 : 3;
  }
}

/** Filtre prix : les champs texte natifs de Shopify portent la valeur envoyée. */
class CvPriceSlider extends CvRangeSlider {
  setup() {
    this.minField = document.getElementById(this.dataset.minInput);
    this.maxField = document.getElementById(this.dataset.maxInput);

    this.minField?.addEventListener('change', () => this.syncFromFields());
    this.maxField?.addEventListener('change', () => this.syncFromFields());
  }

  render() {
    const { min: minValue, max: maxValue } = this.values;

    // Une borne laissée à son extrême vaut "pas de filtre", donc champ vide.
    if (this.minField) this.minField.value = minValue > this.min ? minValue : '';
    if (this.maxField) this.maxField.value = maxValue < this.max ? maxValue : '';
  }

  commit() {
    this.notify();
  }

  syncFromFields() {
    const parse = (field) => {
      if (!field) return null;
      const parsed = parseFloat(field.value.replace(/\s/g, '').replace(',', '.'));
      return Number.isNaN(parsed) ? null : parsed;
    };

    const minValue = parse(this.minField);
    const maxValue = parse(this.maxField);

    this.minRange.value = minValue === null ? this.min : Math.round(minValue);
    this.maxRange.value = maxValue === null ? this.max : Math.round(maxValue);

    this.paint();
  }
}

/**
 * Filtre à paliers (remises). Les cases à cocher réelles restent dans le
 * formulaire, masquées : le slider ne fait que décider lesquelles sont cochées.
 */
class CvBucketSlider extends CvRangeSlider {
  setup() {
    this.minField = document.getElementById(this.dataset.minInput);
    this.maxField = document.getElementById(this.dataset.maxInput);

    const holder = document.getElementById(this.dataset.values);
    this.buckets = holder ? Array.from(holder.querySelectorAll('input[type="checkbox"]')) : [];

    // Les champs affichés ne sont pas envoyés : leur frappe ne doit pas
    // déclencher un re-rendu, seule la validation compte.
    [this.minField, this.maxField].forEach((field) => {
      field?.addEventListener('input', (event) => event.stopPropagation());
      field?.addEventListener('change', () => this.syncFromFields());
    });

    this.render();
  }

  render() {
    const { min: minValue, max: maxValue } = this.values;

    if (this.minField) this.minField.value = minValue;
    if (this.maxField) this.maxField.value = maxValue;
  }

  commit() {
    const { min: minValue, max: maxValue } = this.values;
    // Intervalle complet = aucun palier coché, sinon on filtrerait sur "en
    // promotion" alors que l'utilisateur n'a rien demandé.
    const isFullRange = minValue === this.min && maxValue === this.max;

    this.buckets.forEach((bucket) => {
      const value = Number(bucket.dataset.bucket);
      bucket.checked = !isFullRange && value >= minValue && value <= maxValue;
    });

    this.notify();
  }

  syncFromFields() {
    const parse = (field, fallback) => {
      const parsed = parseInt(String(field?.value ?? '').replace(/[^\d]/g, ''), 10);
      if (Number.isNaN(parsed)) return fallback;
      return Math.min(this.max, Math.max(this.min, parsed));
    };

    let minValue = parse(this.minField, this.min);
    let maxValue = parse(this.maxField, this.max);
    if (minValue > maxValue) [minValue, maxValue] = [maxValue, minValue];

    this.minRange.value = minValue;
    this.maxRange.value = maxValue;

    this.render();
    this.paint();
    this.commit();
  }
}

if (!customElements.get('cv-price-slider')) {
  customElements.define('cv-price-slider', CvPriceSlider);
}

if (!customElements.get('cv-bucket-slider')) {
  customElements.define('cv-bucket-slider', CvBucketSlider);
}
